import React, { useState, useEffect } from 'react';
import Navigation from './Navigation';
import { API_BASE_URL } from '../config/api';
import './common.css';
import './SyncDashboard.css';

function SyncDashboard() {
    const [summary, setSummary] = useState(null);
    const [runs, setRuns] = useState([]);
    const [selectedRun, setSelectedRun] = useState(null);
    const [runDetails, setRunDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState(null);

    // Fetch summary statistics
    const fetchSummary = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/sync-stats/summary`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Failed to fetch summary');

            const data = await response.json();
            setSummary(data);
        } catch (err) {
            console.error('Error fetching summary:', err);
            setError(err.message);
        }
    };

    // Fetch sync runs
    const fetchRuns = async (pageNum = 1) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/sync-stats/runs?page=${pageNum}&limit=20`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Failed to fetch runs');

            const data = await response.json();
            setRuns(data.runs);
            setPagination(data.pagination);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching runs:', err);
            setError(err.message);
            setLoading(false);
        }
    };

    // Fetch details for a specific run
    const fetchRunDetails = async (runId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/sync-stats/runs/${runId}/updates`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Failed to fetch run details');

            const data = await response.json();
            setRunDetails(data.updates);
        } catch (err) {
            console.error('Error fetching run details:', err);
            setError(err.message);
        }
    };

    useEffect(() => {
        fetchSummary();
        fetchRuns(page);
    }, [page]);

    const handleRunClick = (run) => {
        setSelectedRun(run);
        fetchRunDetails(run.id);
    };

    const handleCloseDetails = () => {
        setSelectedRun(null);
        setRunDetails(null);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    const getStatusBadge = (status) => {
        return <span className={`badge badge-${status}`}>{status}</span>;
    };

    const getUpdateTypeBadge = (type) => {
        return <span className={`badge badge-${type}`}>{type}</span>;
    };

    if (loading && !summary) {
        return (
            <>
                <Navigation />
                <div className="page-content">
                    <div className="loading">Loading dashboard...</div>
                </div>
            </>
        );
    }

    return (
        <>
            <Navigation />
            <div className="page-content">
                <div className="page-header">
                    <h2>Sync Statistics Dashboard</h2>
                </div>

                {error && <div className="alert alert-error">{error}</div>}

                {/* Summary Cards */}
                {summary && (
                    <div className="summary-cards">
                        <div className="summary-card">
                            <h3>Total Runs</h3>
                            <p className="summary-value">{summary.summary.total_runs || 0}</p>
                            <p className="summary-label">
                                {summary.summary.successful_runs || 0} successful, {summary.summary.failed_runs || 0} failed
                            </p>
                        </div>

                        <div className="summary-card">
                            <h3>Products Synced</h3>
                            <p className="summary-value">{summary.summary.total_products_synced || 0}</p>
                            <p className="summary-label">
                                Avg: {Math.round(summary.summary.avg_products_per_run || 0)} per run
                            </p>
                        </div>

                        <div className="summary-card">
                            <h3>Last 24 Hours</h3>
                            <p className="summary-value">{summary.recent.runs_last_24h || 0}</p>
                            <p className="summary-label">
                                {summary.recent.products_synced_last_24h || 0} products synced
                            </p>
                        </div>

                        <div className="summary-card">
                            <h3>Update Success Rate</h3>
                            <p className="summary-value">
                                {summary.updates.total_updates > 0
                                    ? Math.round((summary.updates.successful_updates / summary.updates.total_updates) * 100)
                                    : 0}%
                            </p>
                            <p className="summary-label">
                                {summary.updates.successful_updates || 0} / {summary.updates.total_updates || 0}
                            </p>
                        </div>

                        {summary.latestRun && (
                            <div className="summary-card latest-run">
                                <h3>Latest Run</h3>
                                <p className="summary-value">{summary.latestRun.total_synced || 0} synced</p>
                                <p className="summary-label">
                                    {formatDate(summary.latestRun.run_timestamp)}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* Sync Runs Table */}
                <div className="sync-runs-section">
                    <h2>Sync Run History</h2>

                    {runs.length === 0 ? (
                        <p>No sync runs found.</p>
                    ) : (
                        <>
                            <table className="sync-runs-table">
                                <thead>
                                    <tr>
                                        <th>Timestamp</th>
                                        <th>Status</th>
                                        <th>Total Synced</th>
                                        <th>Stale</th>
                                        <th>Risk</th>
                                        <th>API Calls Remaining</th>
                                        <th>Calls/Interval</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {runs.map(run => (
                                        <tr key={run.id} className={run.status === 'failed' ? 'failed-run' : ''}>
                                            <td>{formatDate(run.run_timestamp)}</td>
                                            <td>{getStatusBadge(run.status)}</td>
                                            <td>{run.total_synced}</td>
                                            <td>{run.stale_sync_count}</td>
                                            <td>{run.risk_sync_count}</td>
                                            <td>{run.remaining_ebay_calls}</td>
                                            <td>{run.calls_per_interval}</td>
                                            <td>
                                                <button
                                                    className="btn btn-small btn-primary"
                                                    onClick={() => handleRunClick(run)}
                                                >
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Pagination */}
                            {pagination && pagination.pages > 1 && (
                                <div className="pagination">
                                    <button
                                        onClick={() => setPage(page - 1)}
                                        disabled={page === 1}
                                        className="btn btn-small"
                                    >
                                        Previous
                                    </button>
                                    <span className="page-info">
                                        Page {pagination.page} of {pagination.pages}
                                    </span>
                                    <button
                                        onClick={() => setPage(page + 1)}
                                        disabled={page === pagination.pages}
                                        className="btn btn-small"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Run Details Modal */}
                {selectedRun && (
                    <div className="modal-overlay" onClick={handleCloseDetails}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Sync Run Details</h2>
                                <button className="btn-close" onClick={handleCloseDetails}>&times;</button>
                            </div>

                            <div className="modal-body">
                                <div className="run-info">
                                    <div className="info-row">
                                        <strong>Timestamp:</strong> {formatDate(selectedRun.run_timestamp)}
                                    </div>
                                    <div className="info-row">
                                        <strong>Status:</strong> {getStatusBadge(selectedRun.status)}
                                    </div>
                                    <div className="info-row">
                                        <strong>Stale Products:</strong> {selectedRun.stale_sync_count}
                                    </div>
                                    <div className="info-row">
                                        <strong>Risk Products:</strong> {selectedRun.risk_sync_count}
                                    </div>
                                    <div className="info-row">
                                        <strong>Total Synced:</strong> {selectedRun.total_synced}
                                    </div>
                                    <div className="info-row">
                                        <strong>Remaining API Calls:</strong> {selectedRun.remaining_ebay_calls}
                                    </div>
                                    <div className="info-row">
                                        <strong>Intervals Until Reset:</strong> {selectedRun.intervals_until_reset}
                                    </div>
                                    {selectedRun.error_message && (
                                        <div className="info-row error">
                                            <strong>Error:</strong> {selectedRun.error_message}
                                        </div>
                                    )}
                                </div>

                                {runDetails && (
                                    <div className="product-updates">
                                        <h3>Product Updates ({runDetails.length})</h3>
                                        <table className="updates-table">
                                            <thead>
                                                <tr>
                                                    <th>Type</th>
                                                    <th>Product</th>
                                                    <th>eBay Qty</th>
                                                    <th>Amazon Unshipped</th>
                                                    <th>Buffer</th>
                                                    <th>New Amazon Qty</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {runDetails.map(update => (
                                                    <tr key={update.id} className={update.status === 'failed' ? 'failed-update' : ''}>
                                                        <td>{getUpdateTypeBadge(update.update_type)}</td>
                                                        <td>
                                                            <div className="product-info">
                                                                <div className="product-title">{update.title}</div>
                                                                <div className="product-skus">
                                                                    eBay: {update.ebay_id} | Amazon: {update.amazon_sku}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>{update.ebay_quantity}</td>
                                                        <td>{update.amazon_unshipped}</td>
                                                        <td>{update.amazon_buffer}</td>
                                                        <td><strong>{update.new_amazon_quantity}</strong></td>
                                                        <td>{getStatusBadge(update.status)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export default SyncDashboard;
